import { t } from "@lingui/macro";
import { useAtomValue, useSetAtom } from "jotai";
import { useEffect } from "react";

import { setHostIdAndIndexOnRoundAtom } from "@/screens/BookingDetail/NewPatronBookingFlow/components/EditBooking/atoms/actions/setHostIdAndIndexOnRoundAtom/setHostIdAndIndexOnRoundAtom";
import { getMemberBookingRoundsAtom } from "@/screens/BookingDetail/NewPatronBookingFlow/components/EditBooking/atoms/selectors/getBookingRounds/getBookingMemberRounds";
import { getHostIdAtom } from "@/screens/BookingDetail/NewPatronBookingFlow/components/EditBooking/atoms/selectors/getHostIdAtom/getHostIdAtom";
import { getRoundFromIdAtom } from "@/screens/BookingDetail/NewPatronBookingFlow/components/EditBooking/atoms/selectors/getRoundFromIdAtom/getRoundFromIdAtom";
import { DropdownSelect } from "@whoosh/design-system/src/components/controls";
import { Text } from "@whoosh/design-system/src/components/primitive";

type Props = {
  roundId: string;
};

export const HostingMemberEdit = ({ roundId }: Props) => {
  const currentRound = useAtomValue(getRoundFromIdAtom(roundId));
  const setHostIdAndIndex = useSetAtom(setHostIdAndIndexOnRoundAtom);

  const allMemberBookingRounds = useAtomValue(getMemberBookingRoundsAtom);
  const memberBookingRounds = allMemberBookingRounds.filter(
    (round) => (round.bookingIndex ?? 0) === (currentRound?.bookingIndex ?? 0)
  );
  const hostId = useAtomValue(getHostIdAtom(roundId));

  const hostingMember = memberBookingRounds.find(
    (round) => round.player.id === hostId
  );

  const hostingMemberOptions = memberBookingRounds
    .map((round) => {
      if (
        round.player.clientType === "member" &&
        round.player.firstName &&
        round.player.lastName
      ) {
        return {
          label: `${round.player.firstName} ${round.player.lastName}`,
          value: round.player.id,
        };
      }
      return undefined;
    })
    .filter(
      (option): option is { label: string; value: string } =>
        option !== undefined
    );

  // If hostingMember is undefined, use the last one from hostingMemberOptions
  const effectiveHostingMember =
    hostingMember ??
    (hostingMemberOptions.length > 0
      ? memberBookingRounds.find(
          (round) =>
            round.player.id ===
            hostingMemberOptions[hostingMemberOptions.length - 1].value
        )
      : undefined);

  useEffect(() => {
    if (!hostingMember && effectiveHostingMember) {
      setHostIdAndIndex({
        roundId: currentRound?.localId || "",
        hostId: effectiveHostingMember.player.id ?? "",
      });
    }
  }, [
    hostingMember,
    effectiveHostingMember,
    setHostIdAndIndex,
    currentRound?.localId,
  ]);

  return (
    <>
      <Text
        variant="mainSmallSemiBold"
        color="text-primary"
      >{t`Hosting Member`}</Text>
      <DropdownSelect
        options={hostingMemberOptions}
        value={effectiveHostingMember?.player.id ?? ""}
        onChange={(value) => {
          setHostIdAndIndex({
            roundId: currentRound?.localId || "",
            hostId: value,
          });
        }}
      />
    </>
  );
};
